import React from 'react';
import marketingTheme from '../themes/marketingTheme';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(rgba(36, 36, 42, 0.8), rgba(36, 36, 42, 0.8)), url("/images/mining_data_example.jpg")',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minHeight: '400px',
  display: 'flex',
  alignItems: 'center',
  color: 'white',
}));

const CapabilitySection = styled(Paper)(({ theme }) => ({
  padding: '48px',
  margin: '32px 0',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
  border: '1px solid #E5E7EB',
}));

const BenefitCard = styled(Paper)(({ theme }) => ({
  padding: '32px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  backgroundColor: 'white',
  borderRadius: '8px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
  border: '1px solid #E5E7EB',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
  },
}));

const Mining = () => {
  const benefits = [
    {
      icon: <TrendingUpIcon sx={{ fontSize: '48px', color: marketingTheme.palette.primary.main }} />,
      title: 'Productivity Enhancement',
      description: 'Identify and eliminate SpaceTime wastes in mining operations through continuous visual monitoring.',
    },
    {
      icon: <SecurityIcon sx={{ fontSize: '48px', color: marketingTheme.palette.primary.main }} />,
      title: 'Safety Improvement',
      description: 'Automated detection of safety violations and hazardous conditions in real-time.',
    },
    {
      icon: <SpeedIcon sx={{ fontSize: '48px', color: marketingTheme.palette.primary.main }} />,
      title: 'Operational Efficiency',
      description: 'Transform raw visual data into actionable insights for process optimization.',
    },
  ];

  const capabilities = [
    'Visual detection of time-in-motion and waiting wastes',
    'Automatic operator alerts for adverse conditions',
    'Hard-data measurement replacing manual job cards',
    'Continuous monitoring and situational awareness',
    'Root cause analysis of process inefficiencies',
    'Equipment and process operating condition analysis',
  ];

  return (
    <Box sx={{ pt: 10, backgroundColor: '#F9FAFB' }}>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '36px', md: '48px' },
                  fontWeight: 700,
                  mb: 2,
                  color: 'white',
                }}
              >
                Mining Solutions
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontSize: '20px',
                  fontWeight: 400,
                  color: 'rgba(255, 255, 255, 0.9)',
                  mb: 4,
                }}
              >
                Use visual data to improve productivity.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Benefits Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {benefits.map((benefit, index) => (
            <Grid item xs={12} md={4} key={index}>
              <BenefitCard>
                <Box sx={{ mb: 3 }}>{benefit.icon}</Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1F2937',
                    mb: 2,
                  }}
                >
                  {benefit.title}
                </Typography>
                <Typography
                  sx={{
                    color: '#6B7280',
                    lineHeight: 1.6,
                  }}
                >
                  {benefit.description}
                </Typography>
              </BenefitCard>
            </Grid>
          ))}
        </Grid>

        {/* Capability Statement */}
        <CapabilitySection>
          <Grid container spacing={6}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: '28px',
                  fontWeight: 700,
                  color: '#1F2937',
                  mb: 3,
                }}
              >
                Capability Statement
              </Typography>
              <Typography sx={{ color: '#6B7280', mb: 3, lineHeight: 1.6 }}>
                The vast amounts of data generated by industrial machines and workflows is often 
                underutilized, resulting in lost opportunities for efficiency and cost savings.
              </Typography>
              <Typography sx={{ color: '#6B7280', mb: 3, lineHeight: 1.6 }}>
                Companies lack hard-data on the "SpaceTime wastes" in their processes. 
                Supervisors measure these wastes manually (and sparsely) using job cards.
              </Typography>
              <Typography sx={{ color: '#6B7280', mb: 3, lineHeight: 1.6 }}>
                Our perception technologies can drive bottom-line improvement in industrial 
                operations by visually inferring the root causes of time-in-motion and waiting wastes.
              </Typography>
              <Typography sx={{ color: '#6B7280', lineHeight: 1.6 }}>
                Our learning algorithms extract wisdom from raw visual data. This can be used 
                to improve productivity and safety through continuous monitoring and automatic 
                operator alerts for adverse equipment or process operating conditions.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#1F2937',
                  mb: 3,
                }}
              >
                Key Capabilities
              </Typography>
              <List>
                {capabilities.map((capability, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon>
                      <CheckCircleIcon sx={{ color: marketingTheme.palette.primary.main }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={capability}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: '#6B7280',
                          fontSize: '14px',
                        },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        </CapabilitySection>

        {/* Data Wisdom Section */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#1F2937',
              mb: 4,
            }}
          >
            From Data to Wisdom
          </Typography>
          <Box
            sx={{
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
            }}
          >
            <img
              src="/images/data_information_knowledge_wisdom.png"
              alt="Data Knowledge Information Wisdom"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
              }}
            />
          </Box>
          <Typography sx={{ color: '#6B7280', mb: 4, maxWidth: '700px', mx: 'auto' }}>
            Our camera algorithms are trained to automatically detect visual events which have 
            a bearing on production. This increases situational awareness through hard-data.
          </Typography>
        </Box>

        {/* Industrial Automation */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#1F2937',
              mb: 4,
            }}
          >
            Data-Driven Industrial Automation
          </Typography>
          <Box
            sx={{
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
            }}
          >
            <img
              src="/images/data_driven_industrial_automation_light.png"
              alt="Mining Data Automation"
              style={{
                width: '100%',
                height: 'auto',
                borderRadius: '8px',
              }}
            />
          </Box>
        </Box>

        {/* CTA Section */}
        <Box
          sx={{
            textAlign: 'center',
            backgroundColor: 'white',
            p: 6,
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#1F2937',
              mb: 2,
            }}
          >
            Ready to optimize your mining operations?
          </Typography>
          <Typography
            sx={{
              color: '#6B7280',
              mb: 4,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Transform your mining processes with our AI-powered visual analytics platform.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                backgroundColor: marketingTheme.palette.primary.main,
                '&:hover': { backgroundColor: marketingTheme.palette.primary.dark },
                px: 4,
                py: 1.5,
              }}
            >
              Learn More
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                color: '#1F2937',
                borderColor: '#D1D5DB',
                '&:hover': { backgroundColor: '#F3F4F6' },
                px: 4,
                py: 1.5,
              }}
            >
              Book a Consult
            </Button>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Mining;
